// Instances of an object type ("pew 1", "pew 2", … of "pew"), overlaid in one
// frame to compare them. Each instance's F (object.of) maps its points, as the
// editor shows them in the world, onto the type's frame; fitting finds F by
// trying several turns about the centroid and refining each with the ICP in
// align.ts, keeping the one that matches the reference instance best.
import type { V3 } from '../math.ts';
import { RECORD } from '../lod.ts';
import type { CloudIndex } from '../format.ts';
import { type Rigid, type NN, Grid, apply, compose, fit, identity, transformPoints, yawAbout, yawDeg } from '../align.ts';

/** Points of one instance: packed world xyz, normals (NaN: none) and rgb. */
export interface Pts { xyz: Float32Array; nrm: Float32Array; rgb: Uint8Array }

/** "pew 2" before "pew 10". */
export const natural = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

/** The next free "<type> <n>" among the names in use. */
export function nextInstanceName(type: string, names: Iterable<string>) {
  const re = new RegExp(`^${type.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} (\\d+)$`);
  let n = 0;
  for (const nm of names) { const m = re.exec(nm); if (m) n = Math.max(n, +m[1]); }
  return `${type} ${n + 1}`;
}

export function centroid(xyz: Float32Array): V3 {
  const c: V3 = [0, 0, 0], n = xyz.length / 3;
  for (let i = 0; i < n; i++) for (let k = 0; k < 3; k++) c[k] += xyz[3 * i + k] / n;
  return c;
}

/** Every k-th point so at most cap remain (packed triples). */
export function thin(a: Float32Array, cap: number) {
  const n = a.length / 3;
  if (n <= cap) return a;
  const out = new Float32Array(3 * cap), k = n / cap;
  for (let i = 0; i < cap; i++) out.set(a.subarray(3 * Math.floor(i * k), 3 * Math.floor(i * k) + 3), 3 * i);
  return out;
}

/** The reference for fitting: its points that have normals, and a grid over them. */
export interface Target { grid: NN; qn: Float32Array; c: V3 }

/** Makes the nearest-neighbour index a target searches (a Grid here; the object page's workers use wasm). */
export type MakeNN = (xyz: Float32Array) => NN;
const gridNN: MakeNN = xyz => new Grid(xyz);

export function target(p: Pts, cap = 80000, voxel = 0.007, mk: MakeNN = gridNN): Target | null {
  // points with normals, one per voxel: many overlaid instances make the target very dense, and that
  // much detail adds nothing to a fit but time
  const keep: number[] = [], taken = new Set<number>();
  for (let i = 0; i < p.xyz.length / 3; i++) {
    if (p.nrm[3 * i] !== p.nrm[3 * i]) continue;
    const k = ((Math.floor(p.xyz[3 * i] / voxel) + 32768) * 65536 + (Math.floor(p.xyz[3 * i + 1] / voxel) + 32768)) * 65536 + (Math.floor(p.xyz[3 * i + 2] / voxel) + 32768);
    if (taken.has(k)) continue;
    taken.add(k);
    keep.push(i);
  }
  if (keep.length < 100) return null;
  const step = Math.max(1, keep.length / cap), m = Math.floor(keep.length / step);
  const xyz = new Float32Array(3 * m), qn = new Float32Array(3 * m);
  for (let j = 0; j < m; j++) {
    const i = keep[Math.floor(j * step)];
    xyz.set(p.xyz.subarray(3 * i, 3 * i + 3), 3 * j);
    qn.set(p.nrm.subarray(3 * i, 3 * i + 3), 3 * j);
  }
  return { grid: mk(xyz), qn, c: centroid(xyz) };
}

/** Share of the points (packed, already placed) within d of the target. */
export function matchShare(xyz: Float32Array, t: Target, d = 0.03) {
  const n = xyz.length / 3;
  let k = 0;
  for (let i = 0; i < n; i++) if (t.grid.nearest(xyz[3 * i], xyz[3 * i + 1], xyz[3 * i + 2], d) >= 0) k++;
  return n ? k / n : 0;
}

export interface Fitted { F: Rigid; share: number; rms: number }

/**
 * F putting src (packed world xyz) onto the target: centroids together, then for each
 * of `turns` starting yaws an ICP refine (yaw, x, y, z); the start whose result has
 * the most points within 3 cm wins (ties: lower rms).
 */
export function fitOnto(src: Float32Array, t: Target, turns = 8, cap = 6000): Fitted {
  const s = thin(src, cap), c = centroid(s);
  let best: Fitted | null = null;
  for (let k = 0; k < turns; k++) {
    const start = yawAbout((2 * Math.PI * k) / turns, c, [t.c[0] - c[0], t.c[1] - c[1], t.c[2] - c[2]]);
    const p0 = transformPoints(start, s);
    const r = fit(p0, t.grid, t.qn, { iters: 14, canTurn: true, freeZ: true });
    const F = compose(r.step, start), share = matchShare(transformPoints(F, s), t);
    const rms = Number.isFinite(r.rms1) ? r.rms1 : Infinity;
    if (!best || share > best.share + 1e-9 || (Math.abs(share - best.share) <= 1e-9 && rms < best.rms)) best = { F, share, rms };
  }
  return best!;
}

/** Octahedral normal encode, unit vector -> u8 pair (inverse of math.ts octDecode). */
export function octEncode(x: number, y: number, z: number): [number, number] {
  const s = Math.abs(x) + Math.abs(y) + Math.abs(z) || 1;
  let u = x / s, v = y / s;
  if (z / s < 0) { const a = (1 - Math.abs(v)) * (u >= 0 ? 1 : -1), b = (1 - Math.abs(u)) * (v >= 0 ? 1 : -1); u = a; v = b; }
  const q = (a: number) => Math.max(0, Math.min(255, Math.round((a + 1) * 127.5)));
  return [q(u), q(v)];
}

/** asCloud's optional per-point record bytes (the shading modes' data): the source frame (where hasFrame), capture range, image edge. */
export interface CloudExtras { frame?: Uint32Array | null; hasFrame?: Uint8Array | null; range?: Uint8Array | null; edge?: Uint8Array | null }

/** Packed points as a one-chunk, one-level cloud the viewer can draw as an edit cloud; sp: their spacing (m), which sizes the splats. */
export function asCloud(xyz: Float32Array, nrm: Float32Array, rgb: Uint8Array, sp = 0.01, q: CloudExtras = {}): { idx: CloudIndex; rec: Uint8Array } {
  const n = xyz.length / 3, lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < n; i++) for (let k = 0; k < 3; k++) { lo[k] = Math.min(lo[k], xyz[3 * i + k]); hi[k] = Math.max(hi[k], xyz[3 * i + k]); }
  if (!n) { lo.fill(0); hi.fill(0); }
  const size = lo.map((l, k) => Math.max(1e-3, hi[k] - l)) as V3, origin = lo as V3;
  const rec = new Uint8Array(n * RECORD), d = new DataView(rec.buffer);
  for (let i = 0; i < n; i++) {
    const o = i * RECORD;
    for (let k = 0; k < 3; k++) d.setUint16(o + 2 * k, Math.round(((xyz[3 * i + k] - origin[k]) / size[k]) * 65535), true);
    rec[o + 6] = rgb[3 * i]; rec[o + 7] = rgb[3 * i + 1]; rec[o + 8] = rgb[3 * i + 2];
    const nx = nrm[3 * i];
    if (nx === nx) { rec[o + 9] = 1; [rec[o + 10], rec[o + 11]] = octEncode(nx, nrm[3 * i + 1], nrm[3 * i + 2]); }
    if (q.frame && q.hasFrame?.[i]) rec[o + 9] |= 2 | ((q.frame[i] % 64) << 2);
    if (q.range) rec[o + 12] = q.range[i];
    if (q.edge) rec[o + 13] = q.edge[i];
  }
  const bbox = [...origin, ...origin.map((l, k) => l + size[k])];
  const idx: CloudIndex = {
    version: 2, record: RECORD, spacing: [sp], points: n, bbox, levelOffsets: [0, rec.length], bin: '',
    chunks: [{ origin, size, levelCounts: [n], offsets: [0], spacing: [sp] }],
  };
  return { idx, rec };
}

export interface FrameFit<K> { key: K; W: Rigid; n: number; rms0: number; rms1: number; skip?: string }

/**
 * Per frame: a world correction W moving that frame's points (packed world xyz)
 * onto the target, or a reason it was left alone (too few points, no better,
 * or a move beyond maxShift m / maxYaw degrees, which is more likely a wrong
 * match than a drifted frame). A generator, yielding after each frame.
 */
export function* fitFrames<K>(frames: { key: K; xyz: Float32Array }[], t: Target,
  o: { minPts?: number; maxShift?: number; maxYaw?: number; cap?: number } = {}): Generator<number, FrameFit<K>[]> {
  const minPts = o.minPts ?? 200, maxShift = o.maxShift ?? 0.15, maxYaw = o.maxYaw ?? 8, out: FrameFit<K>[] = [];
  for (const [i, f] of frames.entries()) {
    const n = f.xyz.length / 3, I = identity();
    if (n < minPts) { out.push({ key: f.key, W: I, n, rms0: NaN, rms1: NaN, skip: 'few points' }); yield i; continue; }
    const r = fit(thin(f.xyz, o.cap ?? 4000), t.grid, t.qn, { iters: 10, canTurn: true, freeZ: true });
    const yaw = Math.abs(yawDeg(r.step));
    // the shift of the points themselves (a turn about the far origin shows up as a big t)
    const c = centroid(f.xyz), m = apply(r.step, c), moved = Math.hypot(m[0] - c[0], m[1] - c[1], m[2] - c[2]);
    const skip = !r.accepted || !(r.rms1 < r.rms0) ? 'no better' : moved > maxShift || yaw > maxYaw ? 'too far' : undefined;
    out.push({ key: f.key, W: skip ? I : r.step, n, rms0: r.rms0, rms1: skip ? r.rms0 : r.rms1, ...(skip ? { skip } : {}) });
    yield i;
  }
  return out;
}

/**
 * Per point, how many other instances have a point within r of it: where the overlaid instances agree.
 * inst: each point's instance (index). Points of the same instance never count for each other.
 */
export function support(xyz: Float32Array, inst: ArrayLike<number>, r = 0.03, cap = 150000, from = 0, to = xyz.length / 3): Uint16Array {
  // neighbours from at most cap points (every k-th): plenty to tell which instances are near.
  // Only points from..to are scored (out[i - from]), so the work can be split; the answer doesn't change.
  const n = xyz.length / 3, out = new Uint16Array(to - from), step = Math.max(1, Math.ceil(n / cap)), m = Math.ceil(n / step);
  const sub = new Float32Array(3 * m), subInst = new Int32Array(m);
  for (let j = 0, i = 0; i < n; i += step, j++) { sub.set(xyz.subarray(3 * i, 3 * i + 3), 3 * j); subInst[j] = inst[i]; }
  const g = new Grid(sub, r);
  let ninst = 0;
  for (let i = 0; i < n; i++) ninst = Math.max(ninst, inst[i] + 1);
  const seen = new Int32Array(ninst).fill(-1);
  for (let i = from; i < to; i++) {
    const own = inst[i];
    let c = 0;
    g.within(xyz[3 * i], xyz[3 * i + 1], xyz[3 * i + 2], r, j => { const k = subInst[j]; if (k !== own && seen[k] !== i) { seen[k] = i; c++; } });
    out[i - from] = c;
  }
  return out;
}

/**
 * Point density: per point of xyz, about how many points (itself included) are within r of it. Neighbours
 * are counted in `sample` (every step-th point of the whole cloud) and scaled up by step, so a run can be
 * split over workers, each with its own slice of xyz, and the work stays bounded on big scans.
 * The sample sits in a spatial hash of r-sized cells (flat arrays: a bucket may hold several cells, and the
 * distance test sorts them out); each query, taken in bucket order, looks in its 27 cells' buckets, each once.
 */
export function pointDensity(sample: Float32Array, step: number, xyz: Float32Array, r: number): Uint16Array {
  const m = sample.length / 3, n = xyz.length / 3, out = new Uint16Array(n), inv = 1 / r, r2 = r * r;
  let T = 1024;
  while (T < 2 * m) T *= 2;
  const mask = T - 1, hash = (x: number, y: number, z: number) => (Math.imul(x, 73856093) ^ Math.imul(y, 19349663) ^ Math.imul(z, 83492791)) & mask;
  const start = new Int32Array(T + 1), hs = new Int32Array(m);
  for (let i = 0; i < m; i++) {
    const h = hash(Math.floor(sample[3 * i] * inv), Math.floor(sample[3 * i + 1] * inv), Math.floor(sample[3 * i + 2] * inv));
    hs[i] = h; start[h + 1]++;
  }
  for (let h = 0; h < T; h++) start[h + 1] += start[h];
  const fill = start.slice(0, T), S = new Float32Array(3 * m);
  for (let i = 0; i < m; i++) { const j = 3 * fill[hs[i]]++; S[j] = sample[3 * i]; S[j + 1] = sample[3 * i + 1]; S[j + 2] = sample[3 * i + 2]; }
  // queries in bucket order too: neighbours look in the same buckets, which stay in cache
  const qs = new Int32Array(n), qat = new Int32Array(T + 1), order = new Int32Array(n);
  for (let i = 0; i < n; i++) { const h = hash(Math.floor(xyz[3 * i] * inv), Math.floor(xyz[3 * i + 1] * inv), Math.floor(xyz[3 * i + 2] * inv)); qs[i] = h; qat[h + 1]++; }
  for (let h = 0; h < T; h++) qat[h + 1] += qat[h];
  for (let i = 0; i < n; i++) order[qat[qs[i]]++] = i;
  const seen = new Int32Array(T).fill(-1);
  for (let k = 0; k < n; k++) {
    const i = order[k], x = xyz[3 * i], y = xyz[3 * i + 1], z = xyz[3 * i + 2], cx = Math.floor(x * inv), cy = Math.floor(y * inv), cz = Math.floor(z * inv);
    let c = 0;
    for (let a = -1; a <= 1; a++) for (let b = -1; b <= 1; b++) for (let d = -1; d <= 1; d++) {
      const h = hash(cx + a, cy + b, cz + d);
      if (seen[h] === i) continue;
      seen[h] = i;
      for (let j = 3 * start[h], e = 3 * start[h + 1]; j < e; j += 3) {
        const dx = S[j] - x, dy = S[j + 1] - y, dz = S[j + 2] - z;
        if (dx * dx + dy * dy + dz * dz <= r2) c++;
      }
    }
    out[i] = Math.min(65535, Math.max(1, Math.round(c * step)));
  }
  return out;
}

/** Blue (low) → green → yellow → red (high), for t in [0, 1]. */
export function heat(t: number): [number, number, number] {
  const s = Math.max(0, Math.min(1, t)) * 3, f = (a: number) => Math.round(255 * Math.max(0, Math.min(1, a)));
  return s < 1 ? [f(0.1), f(0.2 + 0.6 * s), f(1 - 0.6 * s)] : s < 2 ? [f(s - 1), f(0.8 + 0.2 * (s - 1)), f(0.4 - 0.4 * (s - 1))] : [f(1), f(1 - (s - 2)), f(0)];
}
